import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  GrantType,
  GrantStatus,
  useCreateGrantMutation,
  useCreateVestingScheduleMutation,
  useUpdateGrantMutation,
  CreateGrantInput,
  CreateVestingScheduleInput,
  UpdateGrantInput,
  UpdateVestingScheduleInput,
  useGetGrantsQuery,
  Grant,
  useUpdateVestingScheduleMutation,
  VestingScheduleType,
  useGetGlobalCompaniesQuery,
  GetGlobalCompaniesQuery,
  GlobalCompanySearchType,
  ExpiryUnitType,
  GrantUploadInput,
  useUploadGrantDataMutation,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import { Modal } from 'react-bootstrap'
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG'
import { toast } from 'react-toastify'
import _ from 'lodash'
import { selectCustomStyles } from '../../../../utils/Select'
import CreatableSelect from 'react-select/creatable'
import { calculateVestedOptions } from './grantUtils'
import { getCSSVariableValue } from '../../../../_metronic/assets/ts/_utils'
import { hexToRGB } from '../../../../utils/HexToRgba'
import Toast, { ToastType } from '../../../../utils/toast'
import { currencies } from '../../../../utils/CurrencyList'

type AddGrantsPopupProps = {
  showModal: boolean
  handleClose: () => void
  userId: number
  grantData?: Partial<Grant>
  onGrantCreated?: (newGrant: Grant) => void
  isEdit?: boolean
  salePrice?: number
}

enum VestingFrequency {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  SemiAnnually = 'Semi_Annually',
  Annually = 'Annually',
}

type UploadError = {
  field: string
  message: string
}

// Track which fields were filled by AI
type AiFilledFields = {
  [key: string]: boolean
}

type SelectOptionType = {
  value: string
  label: string
}

const vestingScheduleMap = {
  '4 years – Annual vesting, 1-year cliff': {
    frequency: VestingFrequency.Annually,
    cliffDurationMonths: 12,
    totalVestingDurationMonths: 48,
  },
  '4 years – Semi-annual vesting, 1-year cliff': {
    frequency: VestingFrequency.SemiAnnually,
    cliffDurationMonths: 12,
    totalVestingDurationMonths: 48,
  },
  '4 years – Quarterly vesting, 1-year cliff': {
    frequency: VestingFrequency.Quarterly,
    cliffDurationMonths: 12,
    totalVestingDurationMonths: 48,
  },
  '4 years – Monthly vesting, 1-year cliff': {
    frequency: VestingFrequency.Monthly,
    cliffDurationMonths: 12,
    totalVestingDurationMonths: 48,
  },
  '4 years – Annual vesting, no cliff': {
    frequency: VestingFrequency.Annually,
    cliffDurationMonths: 0,
    totalVestingDurationMonths: 48,
  },
  '3 years – Annual vesting, 1-year cliff': {
    frequency: VestingFrequency.Annually,
    cliffDurationMonths: 12,
    totalVestingDurationMonths: 36,
  },
  '3 years – Semi-annual vesting, 1-year cliff': {
    frequency: VestingFrequency.SemiAnnually,
    cliffDurationMonths: 12,
    totalVestingDurationMonths: 36,
  },
  '3 years – Quarterly vesting, 1-year cliff': {
    frequency: VestingFrequency.Quarterly,
    cliffDurationMonths: 12,
    totalVestingDurationMonths: 36,
  },
  '3 years – Monthly vesting, 1-year cliff': {
    frequency: VestingFrequency.Monthly,
    cliffDurationMonths: 12,
    totalVestingDurationMonths: 36,
  },
  '3 years – Annual vesting, no cliff': {
    frequency: VestingFrequency.Annually,
    cliffDurationMonths: 0,
    totalVestingDurationMonths: 36,
  },
}

type VestingScheduleData = {
  cliffDurationMonths: number
  scheduleType: string
  totalVestingDurationMonths: number
  vestingFrequency: string
}

function getVestingScheduleByResponse(data: {
  frequency: VestingFrequency
  cliffDurationMonths: number
  totalVestingDurationMonths: number
}): string | null {
  for (const [label, schedule] of Object.entries(vestingScheduleMap)) {
    if (
      schedule.frequency === data.frequency &&
      schedule.cliffDurationMonths === data.cliffDurationMonths &&
      schedule.totalVestingDurationMonths === data.totalVestingDurationMonths
    ) {
      return label
    }
  }
  return null
}

function getExpiryDateFromPeriod(
  grantDate: Date,
  expiryNumber: number,
  expiryUnitType: ExpiryUnitType
): Date {
  const newDate = new Date(grantDate)
  switch (expiryUnitType) {
    case 'DAYS':
      newDate.setDate(newDate.getDate() + expiryNumber)
      break
    case 'MONTHS':
      newDate.setMonth(newDate.getMonth() + expiryNumber)
      break
    case 'YEARS':
      newDate.setFullYear(newDate.getFullYear() + expiryNumber)
      break
    default:
      throw new Error('Invalid expiry unit type')
  }
  return newDate
}

export function AddGrantsPopup(props: AddGrantsPopupProps) {
  const [loading, setLoading] = useState(false)
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [vestingData, setVestingData] = useState<CreateVestingScheduleInput | undefined>()
  const [vestingScheduleId, setVestingScheduleId] = useState<number | undefined>()
  const [isGrantRunning, setIsGrantRunning] = useState(true)
  const [companyOptions, setCompanyOptions] = useState<any[]>([])
  const [allCompanies, setAllCompanies] = useState<GlobalCompanySearchType[]>([])
  const [filteredCompanies, setFilteredCompanies] = useState<GlobalCompanySearchType[]>([])
  const [hissaCompanyId, setHissaCompanyId] = useState<string | undefined>()
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [extractedGrant, setExtractedGrant] = useState<Partial<any> | null>(null)
  const [uploadErrors, setUploadErrors] = useState<UploadError[]>([])
  const [vestingScheduleData, setVestingScheduleData] = useState<VestingScheduleData | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [aiFilledFields, setAiFilledFields] = useState<AiFilledFields>({})

  const currencyList: SelectOptionType[] = []
  currencies.forEach((currency: string) => {
    currencyList.push({
      value: currency,
      label: currency,
    })
  })

  const createGrant = useCreateGrantMutation(graphqlRequestClient)
  const updateGrant = useUpdateGrantMutation(graphqlRequestClient)
  const createVestingSchedule = useCreateVestingScheduleMutation(graphqlRequestClient)
  const updateVestingSchedule = useUpdateVestingScheduleMutation(graphqlRequestClient)
  const uploadGrantData = useUploadGrantDataMutation<Error>(
    graphqlRequestClient,
    {
      onMutate: () => {
        setIsUploading(true)
      },
      onSuccess: (data) => {
        if (
          data.uploadGrantData &&
          Object.keys(data.uploadGrantData).length > 0 &&
          data.uploadGrantData.companyName
        ) {
          setExtractedGrant(data.uploadGrantData)
          Toast('Grant data extracted successfully', ToastType.success)
        } else {
          Toast(
            'Error uploading the file. Please check the format of the document and re-upload.',
            ToastType.error
          )
          setUploadedFile(null)
        }
        setIsUploading(false)
      },
      onError: (error: any) => {
        console.error('Mutation error:', error)
        if (error.response?.errors && Array.isArray(error.response.errors)) {
          const fieldErrors: UploadError[] = error.response.errors.map((err: any) => ({
            field: err.extensions?.field || 'file',
            message: err.message || 'Error processing file',
          }))
          setUploadErrors(fieldErrors)
        } else {
          Toast(
            'Error uploading the file. Please check the format of the document and re-upload.',
            ToastType.error
          )
        }
        setUploadedFile(null)
        setIsUploading(false)
      },
    },
    {}
  )

  const { data: globalCompanies } = useGetGlobalCompaniesQuery<GetGlobalCompaniesQuery, Error>(
    graphqlRequestClient,
    {},
    {
      onSuccess: () => {
        setIsCompaniesLoading(false)
      },
      onError: () => {
        setIsCompaniesLoading(false)
      },
    }
  )

  const getAiHighlightStyles = (fieldName: string) => {
    if (aiFilledFields[fieldName]) {
      return {
        backgroundColor: '#e8f4f8',
        borderColor: '#1976d2',
        borderWidth: '2px',
        position: 'relative' as const,
      }
    }
    return {}
  }

  useEffect(() => {
    if (vestingScheduleData) {
      const matchingTemplate = getVestingScheduleByResponse({
        frequency: vestingScheduleData.vestingFrequency as VestingFrequency,
        cliffDurationMonths: vestingScheduleData.cliffDurationMonths,
        totalVestingDurationMonths: vestingScheduleData.totalVestingDurationMonths,
      })

      if (matchingTemplate) {
        const selected = matchingTemplate as keyof typeof vestingScheduleMap
        if (selected) {
          const mapping = vestingScheduleMap[selected]

          if (mapping) {
            formik.setFieldValue('vestingTemplateName', selected)
            formik.setFieldValue('cliffDurationMonths', mapping.cliffDurationMonths.toString())
            formik.setFieldValue('vestingFrequency', mapping.frequency)
            formik.setFieldValue(
              'totalVestingDurationMonths',
              mapping.totalVestingDurationMonths.toString()
            )
            if (formik.values.grantDate && formik.values.numberGranted) {
              const vested = calculateVestedOptions(
                new Date(formik.values.grantDate),
                Number(formik.values.numberGranted),
                mapping.frequency,
                mapping.cliffDurationMonths,
                mapping.totalVestingDurationMonths,
                new Date()
              )
              formik.setFieldValue('vestedOptions', vested.toString())
            }
          }
        }
      }
    }
  }, [vestingScheduleData])

  useEffect(() => {
    if (extractedGrant) {
      const grant = extractedGrant
      const vestingSchedule = extractedGrant.vestingSchedule
      const newAiFilledFields: AiFilledFields = {}

      let vestingTemplateName = ''

      if (vestingSchedule) {
        vestingTemplateName =
          getVestingScheduleByResponse({
            frequency: vestingSchedule.vestingFrequency as VestingFrequency,
            cliffDurationMonths: vestingSchedule.cliffDurationMonths,
            totalVestingDurationMonths: vestingSchedule.totalVestingDurationMonths,
          }) || ''
      }

      const newValues = {
        ...formik.values,
        companyName: grant.companyName?.trim() || formik.values.companyName,
        companyCurrency:
          grant.companyCurrency?.trim() || formik.values.companyCurrency || 'INR - ₹',
        grantDate:
          grant.grantDate && !isNaN(new Date(grant.grantDate).getTime())
            ? new Date(grant.grantDate).toISOString().split('T')[0]
            : formik.values.grantDate,
        grantType:
          grant.grantType && Object.values(GrantType).includes(grant.grantType as GrantType)
            ? grant.grantType
            : formik.values.grantType,
        numberGranted:
          grant.numberGranted != null && !isNaN(Number(grant.numberGranted))
            ? Number(grant.numberGranted).toString()
            : formik.values.numberGranted,
        exercisePriceOrSarValue:
          grant.exercisePriceOrSarValue != null && !isNaN(Number(grant.exercisePriceOrSarValue))
            ? Number(grant.exercisePriceOrSarValue).toString()
            : formik.values.exercisePriceOrSarValue,
        expiryDate:
          grant.expiryDate && !isNaN(new Date(grant.expiryDate).getTime())
            ? new Date(grant.expiryDate).toISOString().split('T')[0]
            : formik.values.expiryDate,
        companyFMV:
          grant.companyFMV != null && !isNaN(Number(grant.companyFMV))
            ? Number(grant.companyFMV).toString()
            : formik.values.companyFMV,
        salePrice:
          grant.salePrice != null && !isNaN(Number(grant.salePrice))
            ? Number(grant.salePrice).toString()
            : formik.values.salePrice,
        planName: grant.planName?.trim() || formik.values.planName,
        companyDate:
          grant.fmvDate && !isNaN(new Date(grant.fmvDate).getTime())
            ? new Date(grant.fmvDate).toISOString().split('T')[0]
            : formik.values.companyDate,
        amountExercised:
          grant.numberExercised != null && !isNaN(Number(grant.numberExercised))
            ? Number(grant.numberExercised).toString()
            : formik.values.amountExercised,
        vestedOptions:
          grant.vestedOptions != null && !isNaN(Number(grant.vestedOptions))
            ? Number(grant.vestedOptions).toString()
            : '',
        expiryNumber:
          grant.expiryNumber != null && !isNaN(Number(grant.expiryNumber))
            ? Number(grant.expiryNumber).toString()
            : formik.values.expiryNumber,
        expiryUnitType:
          grant.expiryUnitType &&
          Object.values(ExpiryUnitType).includes(grant.expiryUnitType as ExpiryUnitType)
            ? grant.expiryUnitType
            : formik.values.expiryUnitType,
        vestingTemplateName,
        notes: grant.notes?.trim() || formik.values.notes,
        totalVestingDurationMonths:
          vestingSchedule?.totalVestingDurationMonths != null &&
          !isNaN(Number(vestingSchedule.totalVestingDurationMonths))
            ? Number(vestingSchedule.totalVestingDurationMonths).toString()
            : formik.values.totalVestingDurationMonths,
        cliffDurationMonths:
          vestingSchedule?.cliffDurationMonths != null &&
          !isNaN(Number(vestingSchedule.cliffDurationMonths))
            ? Number(vestingSchedule.cliffDurationMonths).toString()
            : formik.values.cliffDurationMonths,
        vestingFrequency:
          vestingSchedule?.vestingFrequency &&
          Object.values(VestingFrequency).includes(
            vestingSchedule.vestingFrequency as VestingFrequency
          )
            ? vestingSchedule.vestingFrequency
            : formik.values.vestingFrequency,
      }

      if (grant.companyName?.trim() && grant.companyName?.trim() !== formik.values.companyName) {
        newAiFilledFields.companyName = true
      }
      if (
        grant.companyCurrency?.trim() &&
        grant.companyCurrency?.trim() !== formik.values.companyCurrency
      ) {
        newAiFilledFields.companyCurrency = true
      }
      if (
        grant.grantDate &&
        !isNaN(new Date(grant.grantDate).getTime()) &&
        new Date(grant.grantDate).toISOString().split('T')[0] !== formik.values.grantDate
      ) {
        newAiFilledFields.grantDate = true
      }
      if (
        grant.grantType &&
        Object.values(GrantType).includes(grant.grantType as GrantType) &&
        grant.grantType !== formik.values.grantType
      ) {
        newAiFilledFields.grantType = true
      }
      if (
        grant.numberGranted != null &&
        !isNaN(Number(grant.numberGranted)) &&
        Number(grant.numberGranted).toString() !== formik.values.numberGranted
      ) {
        newAiFilledFields.numberGranted = true
      }
      if (
        grant.exercisePriceOrSarValue != null &&
        !isNaN(Number(grant.exercisePriceOrSarValue)) &&
        Number(grant.exercisePriceOrSarValue).toString() !== formik.values.exercisePriceOrSarValue
      ) {
        newAiFilledFields.exercisePriceOrSarValue = true
      }
      if (
        grant.expiryDate &&
        !isNaN(new Date(grant.expiryDate).getTime()) &&
        new Date(grant.expiryDate).toISOString().split('T')[0] !== formik.values.expiryDate
      ) {
        newAiFilledFields.expiryDate = true
      }
      if (
        grant.companyFMV != null &&
        !isNaN(Number(grant.companyFMV)) &&
        Number(grant.companyFMV).toString() !== formik.values.companyFMV
      ) {
        newAiFilledFields.companyFMV = true
      }
      if (grant.planName?.trim() && grant.planName?.trim() !== formik.values.planName) {
        newAiFilledFields.planName = true
      }
      if (
        grant.fmvDate &&
        !isNaN(new Date(grant.fmvDate).getTime()) &&
        new Date(grant.fmvDate).toISOString().split('T')[0] !== formik.values.companyDate
      ) {
        newAiFilledFields.companyDate = true
      }
      if (
        grant.numberExercised != null &&
        !isNaN(Number(grant.numberExercised)) &&
        Number(grant.numberExercised).toString() !== formik.values.amountExercised
      ) {
        newAiFilledFields.amountExercised = true
      }
      if (
        grant.vestedOptions != null &&
        !isNaN(Number(grant.vestedOptions)) &&
        Number(grant.vestedOptions).toString() !== formik.values.vestedOptions
      ) {
        newAiFilledFields.vestedOptions = true
      }
      if (
        grant.expiryNumber != null &&
        !isNaN(Number(grant.expiryNumber)) &&
        Number(grant.expiryNumber).toString() !== formik.values.expiryNumber
      ) {
        newAiFilledFields.expiryNumber = true
      }
      if (
        grant.expiryUnitType &&
        Object.values(ExpiryUnitType).includes(grant.expiryUnitType as ExpiryUnitType) &&
        grant.expiryUnitType !== formik.values.expiryUnitType
      ) {
        newAiFilledFields.expiryUnitType = true
      }
      if (grant.notes?.trim() && grant.notes?.trim() !== formik.values.notes) {
        newAiFilledFields.notes = true
      }
      if (vestingTemplateName && vestingTemplateName !== formik.values.vestingTemplateName) {
        newAiFilledFields.vestingTemplateName = true
      }

      if (vestingSchedule) {
        if (
          vestingSchedule.totalVestingDurationMonths != null &&
          !isNaN(Number(vestingSchedule.totalVestingDurationMonths)) &&
          Number(vestingSchedule.totalVestingDurationMonths).toString() !==
            formik.values.totalVestingDurationMonths
        ) {
          newAiFilledFields.totalVestingDurationMonths = true
        }
        if (
          vestingSchedule.cliffDurationMonths != null &&
          !isNaN(Number(vestingSchedule.cliffDurationMonths)) &&
          Number(vestingSchedule.cliffDurationMonths).toString() !==
            formik.values.cliffDurationMonths
        ) {
          newAiFilledFields.cliffDurationMonths = true
        }
        if (
          vestingSchedule.vestingFrequency &&
          Object.values(VestingFrequency).includes(
            vestingSchedule.vestingFrequency as VestingFrequency
          ) &&
          vestingSchedule.vestingFrequency !== formik.values.vestingFrequency
        ) {
          newAiFilledFields.vestingFrequency = true
        }
      }

      setAiFilledFields(newAiFilledFields)
      formik.setValues(newValues, true)

      if (vestingSchedule) {
        const template = vestingScheduleMap[vestingTemplateName as keyof typeof vestingScheduleMap]
        if (template && newValues.grantDate && newValues.numberGranted) {
          const vested = calculateVestedOptions(
            new Date(newValues.grantDate),
            Number(newValues.numberGranted),
            template.frequency,
            template.cliffDurationMonths,
            template.totalVestingDurationMonths,
            new Date()
          )
          formik.setFieldValue('vestedOptions', vested.toString())
        }

        setVestingData({
          grantId: grant.grantId || 0,
          scheduleType: vestingTemplateName
            ? VestingScheduleType.TimeBased
            : VestingScheduleType.Manual,
          cliffDurationMonths: Number(vestingSchedule.cliffDurationMonths) || 0,
          totalVestingDurationMonths: Number(vestingSchedule.totalVestingDurationMonths) || 0,
          vestingFrequency:
            (vestingSchedule.vestingFrequency as VestingFrequency) || VestingFrequency.Monthly,
          vestingTemplateName,
          vestedOptions: Number(vestingSchedule.vestedOptions) || 0,
        })
        setVestingScheduleId(vestingSchedule.vestingScheduleId)
      }

      setIsGrantRunning(grant.status === GrantStatus.Active)
    }
  }, [extractedGrant])

  const handleFieldChange = (fieldName: string, value: any) => {
    setAiFilledFields((prev) => ({
      ...prev,
      [fieldName]: false,
    }))

    formik.setFieldValue(fieldName, value)
  }

  useEffect(() => {
    if (globalCompanies && globalCompanies.globalCompaniesSearch) {
      setAllCompanies(
        _.cloneDeep(globalCompanies.globalCompaniesSearch) as GlobalCompanySearchType[]
      )
      setIsCompaniesLoading(false)
    }
  }, [globalCompanies])

  function getSearchedCompanies(search: string) {
    const startsWithCompanies = allCompanies.filter((company) =>
      company.name?.toLowerCase().startsWith(search.toLowerCase())
    )
    const includesWithCompanies = allCompanies.filter(
      (company) =>
        company.name?.toLowerCase().includes(search.toLowerCase()) &&
        !company.name.toLowerCase().startsWith(search.toLowerCase())
    )
    const filteredResult = _.union(startsWithCompanies, includesWithCompanies)
    setFilteredCompanies(search === '' ? [] : filteredResult)
  }

  useEffect(() => {
    const options = filteredCompanies.map((company) => ({
      key: company.name ?? undefined,
      value: company.name ?? undefined,
      label: company.name ?? undefined,
      companyName: company.name ?? undefined,
      cin: company.cin ?? undefined,
      hissaCompanyId: company.id ?? undefined,
      fmv: company.sharePrice ?? undefined,
      fmvDate: company.valuationDate ?? undefined,
      currency: company.companyCurrency ?? undefined,
    }))
    setCompanyOptions(options)
  }, [filteredCompanies])

  useEffect(() => {
    const grant = props.grantData
    const vesting = grant?.vestingSchedules?.[0]

    if (props.isEdit && grant) {
      if (vesting) {
        setVestingData({
          grantId: grant.grantId!,
          scheduleType: vesting.scheduleType,
          cliffDurationMonths: vesting.cliffDurationMonths,
          totalVestingDurationMonths: vesting.totalVestingDurationMonths,
          vestingFrequency: vesting.vestingFrequency,
          vestingTemplateName: vesting.vestingTemplateName,
          vestedOptions: vesting.vestedOptions,
        })
        setVestingScheduleId(vesting.vestingScheduleId)
      }
      setIsGrantRunning(grant.status === GrantStatus.Active)
    }
  }, [props.isEdit, props.grantData])

  const initialValues = {
    companyName: props.grantData?.companyName ?? '',
    companyCurrency: props.grantData?.companyCurrency ?? 'INR - ₹',
    grantDate: props.grantData?.grantDate
      ? new Date(props.grantData.grantDate).toISOString().split('T')[0]
      : '',
    grantType: props.grantData?.grantType ?? '',
    numberGranted: props.grantData?.numberGranted?.toString() ?? '',
    exercisePriceOrSarValue: props.grantData?.exercisePriceOrSarValue?.toString() ?? '',
    expiryDate: props.grantData?.expiryDate
      ? new Date(props.grantData.expiryDate).toISOString().split('T')[0]
      : '',
    companyFMV: props.grantData?.companyFMV?.toString() ?? '',
    salePrice: props.grantData?.salePrice?.toString() ?? '',
    planName: props.grantData?.planName ?? '',
    totalVestingDurationMonths: vestingData?.totalVestingDurationMonths?.toString() ?? '',
    cliffDurationMonths: vestingData?.cliffDurationMonths?.toString() ?? '',
    vestingFrequency: vestingData?.vestingFrequency ?? ('' as VestingFrequency | ''),
    companyDate: props.grantData?.fmvDate
      ? new Date(props.grantData.fmvDate).toISOString().split('T')[0]
      : '',
    amountExercised: props.grantData?.numberExercised?.toString() ?? '',
    vestedOptions: props.grantData?.vestedOptions?.toString() ?? '',
    expiryNumber: props.grantData?.expiryNumber?.toString() ?? '',
    expiryUnitType: props.grantData?.expiryUnitType as ExpiryUnitType,
    vestingTemplateName: props.grantData?.vestingSchedules?.[0]?.vestingTemplateName ?? '',
    notes: props.grantData?.notes?.toString() ?? '',
  }

  const validationSchema = (isGrantRunning: boolean) =>
    Yup.object().shape({
      companyName: Yup.string().required('Company Name is required'),
      companyCurrency: Yup.string().required('Company Currency is required'),
      companyFMV: Yup.number().min(0, 'FMV cannot be negative').nullable(),
      grantDate: Yup.string().required('Grant Date is required'),
      grantType: Yup.string().oneOf(Object.values(GrantType)).required('Grant Type is required'),
      numberGranted: Yup.number().required('Number Granted is required').min(1),
      exercisePriceOrSarValue: Yup.number().required('Strike Price is required').min(0),
      planName: Yup.string().required('Plan Name is required'),
      totalVestingDurationMonths: Yup.number().when(['$isGrantRunning', 'vestingTemplateName'], {
        is: (isGrantRunning: boolean, vestingTemplateName: string) =>
          isGrantRunning && vestingTemplateName,
        then: Yup.number().required('Total Duration required').moreThan(0),
        otherwise: Yup.number().nullable(),
      }),
      cliffDurationMonths: Yup.number().when(['$isGrantRunning', 'vestingTemplateName'], {
        is: (isGrantRunning: boolean, vestingTemplateName: string) =>
          isGrantRunning && vestingTemplateName,
        then: Yup.number().required('Cliff Duration required').min(0),
        otherwise: Yup.number().nullable(),
      }),
      vestingFrequency: Yup.string().when(['$isGrantRunning', 'vestingTemplateName'], {
        is: (isGrantRunning: boolean, vestingTemplateName: string) =>
          isGrantRunning && vestingTemplateName,
        then: Yup.string()
          .required('Vesting Frequency is required')
          .oneOf(Object.values(VestingFrequency), 'Invalid vesting frequency'),
        otherwise: Yup.string().nullable(),
      }),
      vestedOptions: Yup.number()
        .when(['$isGrantRunning', 'vestingTemplateName'], {
          is: (isGrantRunning: boolean, vestingTemplateName: string) =>
            isGrantRunning && !vestingTemplateName,
          then: Yup.number()
            .required('Vested Options is required')
            .min(0, 'Cannot be negative')
            .typeError('Must be a number'),
          otherwise: Yup.number()
            .min(0, 'Cannot be negative')
            .typeError('Must be a number')
            .nullable(),
        })
        .when('$isGrantRunning', {
          is: false,
          then: Yup.number()
            .required('Vested Options is required')
            .min(0, 'Cannot be negative')
            .typeError('Must be a number'),
        })
        .test(
          'less-than-or-equal-to-granted',
          'Vested options cannot exceed number granted',
          function (value) {
            const { numberGranted } = this.parent
            const granted = Number(numberGranted)
            return value === undefined || value <= granted
          }
        ),
      amountExercised: Yup.number()
        .required('Amount Exercised is required')
        .min(0, 'Cannot be negative')
        .typeError('Must be a number')
        .test(
          'less-than-vested',
          'Exercised options cannot exceed vested options',
          function (value) {
            const { vestingTemplateName, vestedOptions } = this.parent
            const vested = Number(vestedOptions)
            return value === undefined || value <= vested
          }
        ),
      notes: Yup.string().max(500, 'Notes cannot exceed 500 characters').nullable(),
    })

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema(isGrantRunning),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setLoading(true)

      if (values.expiryNumber && values.expiryUnitType && values.grantDate) {
        const expiryDate = getExpiryDateFromPeriod(
          new Date(values.grantDate),
          Number(values.expiryNumber),
          values.expiryUnitType as ExpiryUnitType
        )
        values.expiryDate = expiryDate.toISOString().split('T')[0]
        values.expiryUnitType = values.expiryUnitType as ExpiryUnitType
      }

      try {
        const sharedInput = {
          companyName: values.companyName,
          companyCurrency: values.companyCurrency,
          grantDate: new Date(values.grantDate).toISOString(),
          grantType: values.grantType as GrantType,
          numberGranted: Number(values.numberGranted),
          exercisePriceOrSarValue: Number(values.exercisePriceOrSarValue),
          expiryDate: values.expiryDate ? new Date(values.expiryDate).toISOString() : null,
          companyFMV:
            values.companyFMV === null || values.companyFMV?.toString().trim() === ''
              ? null
              : Number(values.companyFMV),
          salePrice: values.salePrice ? Number(values.salePrice) : undefined,
          numberExercised: values.amountExercised ? Number(values.amountExercised) : 0,
          fmvDate: values.companyDate ? new Date(values.companyDate).toISOString() : null,
          expiryNumber: values.expiryNumber ? Number(values.expiryNumber) : null,
          notes: values.notes || undefined,
          expiryUnitType: values.expiryUnitType ? (values.expiryUnitType as ExpiryUnitType) : null,
          vestedOptions:
            values.vestedOptions !== '' && values.vestedOptions !== null
              ? Number(values.vestedOptions)
              : null,
          status: isGrantRunning ? GrantStatus.Active : GrantStatus.InActive,
          planName: values.planName,
        }

        let grantId = props.isEdit ? props.grantData?.grantId : undefined
        let finalGrant: Grant | undefined

        if (props.isEdit && grantId) {
          const vestingInput: UpdateVestingScheduleInput = {
            vestingScheduleId,
            scheduleType: values.vestingTemplateName
              ? VestingScheduleType.TimeBased
              : VestingScheduleType.Manual,
            totalVestingDurationMonths: values.vestingTemplateName
              ? Number(values.totalVestingDurationMonths)
              : undefined,
            cliffDurationMonths: values.vestingTemplateName
              ? Number(values.cliffDurationMonths)
              : undefined,
            vestingTemplateName: values.vestingTemplateName,
            vestedOptions: values.vestedOptions ? Number(values.vestedOptions) : undefined,
            vestingFrequency: values.vestingTemplateName
              ? (values.vestingFrequency as VestingFrequency)
              : VestingFrequency.Monthly,
            metadata: undefined,
          }
          const updateInput: UpdateGrantInput = {
            ...sharedInput,
            vestingSchedule: {
              ...vestingInput,
              vestingScheduleId,
              vestingFrequency: (vestingInput.vestingFrequency as VestingFrequency) || null,
            },
            grantId,
            cancellationReason: '',
            metadata: '',
          }

          await updateGrant.mutateAsync({ input: updateInput })
          Toast('Grant updated successfully', ToastType.success)

          if (vestingScheduleId) {
            const updateVestingInput: UpdateVestingScheduleInput = {
              vestingScheduleId,
              scheduleType: values.vestingTemplateName
                ? VestingScheduleType.TimeBased
                : VestingScheduleType.Manual,
              totalVestingDurationMonths: values.vestingTemplateName
                ? Number(values.totalVestingDurationMonths)
                : undefined,
              cliffDurationMonths: values.vestingTemplateName
                ? Number(values.cliffDurationMonths)
                : undefined,
              vestingTemplateName: values.vestingTemplateName,
              vestedOptions: values.vestedOptions ? Number(values.vestedOptions) : undefined,
              vestingFrequency: values.vestingTemplateName
                ? (values.vestingFrequency as VestingFrequency)
                : undefined,
              metadata: undefined,
            }

            await updateVestingSchedule.mutateAsync({ input: updateVestingInput })
          } else if (grantId) {
            const vestingInput: CreateVestingScheduleInput = {
              grantId,
              scheduleType: values.vestingTemplateName
                ? VestingScheduleType.TimeBased
                : VestingScheduleType.Manual,
              totalVestingDurationMonths: values.vestingTemplateName
                ? Number(values.totalVestingDurationMonths)
                : undefined,
              cliffDurationMonths: values.vestingTemplateName
                ? Number(values.cliffDurationMonths)
                : undefined,
              vestingTemplateName: values.vestingTemplateName,
              vestedOptions: values.vestedOptions ? Number(values.vestedOptions) : undefined,
              vestingFrequency: values.vestingTemplateName
                ? (values.vestingFrequency as VestingFrequency)
                : undefined,
            }

            await createVestingSchedule.mutateAsync({ input: vestingInput })
            // Toast('Vesting schedule added', ToastType.success)
          }
        } else {
          const createInput: CreateGrantInput = {
            ...sharedInput,
            userId: props.userId,
          }

          const res = await createGrant.mutateAsync({ input: createInput })
          grantId = res.createGrant?.grantId
          finalGrant = res.createGrant as Grant
          Toast('Grant created successfully', ToastType.success)

          if (grantId) {
            const vestingInput: CreateVestingScheduleInput = {
              grantId,
              scheduleType: values.vestingTemplateName
                ? VestingScheduleType.TimeBased
                : VestingScheduleType.Manual,
              totalVestingDurationMonths: values.vestingTemplateName
                ? Number(values.totalVestingDurationMonths)
                : undefined,
              cliffDurationMonths: values.vestingTemplateName
                ? Number(values.cliffDurationMonths)
                : undefined,
              vestingTemplateName: values.vestingTemplateName,
              vestedOptions: values.vestedOptions ? Number(values.vestedOptions) : undefined,
              vestingFrequency: values.vestingTemplateName
                ? (values.vestingFrequency as VestingFrequency)
                : undefined,
            }

            await createVestingSchedule.mutateAsync({ input: vestingInput })
          }
        }

        await queryClient.invalidateQueries({
          queryKey: useGetGrantsQuery.getKey({}),
        })

        props.onGrantCreated?.(finalGrant!)
        props.handleClose()
      } catch (err) {
        console.error(err)
        toast.error('Something went wrong.')
      } finally {
        setLoading(false)
      }
    },
  })

  const handleFileUpload = async (file: File) => {
  formik.resetForm({ values: initialValues })
  setAiFilledFields({})
  setExtractedGrant(null)
  setUploadErrors([])
  setUploadedFile(null)
  setFileError(null)

  setUploadedFile(file)

  const validFileTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
  const isValid =
    validFileTypes.includes(file.type) || file.name.toLowerCase().match(/\.(jpg|jpeg)$/)

  if (!isValid) {
    Toast('Invalid file. Allowed formats: PDF, PNG, JPEG, or JPG.', ToastType.error)
    setTimeout(() => {
      setUploadedFile(null)
    }, 2000)
    return
  }

  const uploadInput: GrantUploadInput = { file }

  uploadGrantData.mutate(
    { input: uploadInput },
    {
      onError: (error: any) => {
        console.error('Upload failed:', error)
        Toast('Failed to upload file. Please try again.', ToastType.error)
        setFileError('Upload failed. Please try again.')
        setUploadedFile(null)
        setTimeout(() => {
      setFileError(null)
    }, 2000)
      setTimeout(() => {
      setUploadedFile(null)
    }, 2000)
    return
      },
    }
  )
}


  useEffect(() => {
    if (
      formik.values.vestingTemplateName &&
      formik.values.grantDate &&
      formik.values.numberGranted
    ) {
      const template =
        vestingScheduleMap[formik.values.vestingTemplateName as keyof typeof vestingScheduleMap]
      if (template) {
        const vested = calculateVestedOptions(
          new Date(formik.values.grantDate),
          Number(formik.values.numberGranted),
          template.frequency,
          template.cliffDurationMonths,
          template.totalVestingDurationMonths,
          new Date()
        )
        formik.setFieldValue('vestedOptions', vested.toString())
        formik.setFieldValue('cliffDurationMonths', template.cliffDurationMonths.toString())
        formik.setFieldValue('vestingFrequency', template.frequency)
        formik.setFieldValue(
          'totalVestingDurationMonths',
          template.totalVestingDurationMonths.toString()
        )
        const amountExercised = Number(formik.values.amountExercised)
        if (amountExercised > vested) {
          formik.setFieldError(
            'amountExercised',
            `Exercised options (${amountExercised}) cannot exceed vested options (${vested})`
          )
        }
      }
    } else {
      if (formik.values.vestedOptions) {
        formik.setFieldValue('vestedOptions', formik.values.vestedOptions)
      } else {
        formik.setFieldValue('vestedOptions', '')
      }
      formik.setFieldValue('cliffDurationMonths', '')
      formik.setFieldValue('vestingFrequency', '')
      formik.setFieldValue('totalVestingDurationMonths', '')
    }
  }, [formik.values.vestingTemplateName, formik.values.grantDate, formik.values.numberGranted])

  return (
    <Modal
      id='kt_modal_add_grant'
      show={props.showModal}
      onHide={props.handleClose}
      dialogClassName='modal-dialog modal-dialog-centered modal-lg mw-md-650px mw-sm-100'
      scrollable
    >
      <div className='d-flex justify-content-between align-items-center px-6 px-md-9 pt-6'>
        <h2 className='fw-bolder text-dark m-0 fs-4 fs-md-3'>
          {props.isEdit ? 'Edit Grant' : 'Create Grant'}
        </h2>
        <button
          className='btn btn-sm btn-icon btn-active-light-primary'
          onClick={props.handleClose}
        >
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </button>
      </div>
      <Modal.Body className='p-6 p-md-9'>
        <div
          className={`card border border-2 border-dashed ${
            uploadedFile ? 'border-primary bg-gray-200' : 'border-gray-400'
          } rounded-2 mb-3 mb-md-4`}
        >
          <div
            className='card-body p-5 text-center position-relative'
            onDragOver={(e) => {
              e.preventDefault()
              e.currentTarget.classList.add('border-primary', 'bg-light')
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              e.currentTarget.classList.remove('border-primary', 'bg-light')
            }}
            onDrop={(e) => {
              e.preventDefault()
              e.currentTarget.classList.remove('border-primary', 'bg-light')
              const file = e.dataTransfer.files[0] || null
              if (file) {
                handleFileUpload(file)
              }
            }}
          >
            {isUploading ? (
              <div className='d-flex align-items-center justify-content-center flex-column'>
                <div className='spinner-border spinner-border-sm text-primary mb-2' role='status'>
                  <span className='visually-hidden'>Uploading...</span>
                </div>
                <p className='text-muted fs-7 mb-2'>Uploading and processing file...</p>
              </div>
            ) : (
              <>
                <label htmlFor='file-upload' className='d-block mb-2'>
                  <span className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'>
                    <i className='bi bi-upload fs-2'></i>
                  </span>
                </label>
                <p className='text mb-2'>Upload Grant Letter or Image</p>
                <p className='text-gray-600 small'>PDF, PNG, JPEG, or JPG files accepted</p>
                <input
                  id='file-upload'
                  type='file'
                  accept='.pdf,.png,.jpeg,.jpg'
                  className='d-none'
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null
                    if (file) {
                      handleFileUpload(file)
                    }
                    e.target.value = ''
                  }}
                />
                <button
                  type='button'
                  className='btn btn-outline btn-sm me-2'
                  onClick={() => document.getElementById('file-upload')?.click()}
                  disabled={isUploading}
                >
                  Choose File
                </button>
                <div className='position-relative d-inline-block'>
                  {uploadedFile && (
                    <span
                      className='badge fs-8 fw-semibold mb-1 me-2 d-flex align-items-center gap-3 py-3 px-2 text-popover'
                      style={{
                        background: hexToRGB(getCSSVariableValue('--kt-donut-' + (3 % 10)), 0.3),
                      }}
                    >
                      {uploadedFile.name}
                      <div
                        onClick={() => {
                          setUploadedFile(null)
                          setExtractedGrant(null)
                          setUploadErrors([])
                          setAiFilledFields({})
                          formik.resetForm({ values: initialValues })
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <KTSVG
                          className='svg-icon-1 text-gray-header'
                          path='/media/icons/duotune/general/gen040.svg'
                        />
                      </div>
                    </span>
                  )}
                  {fileError && <div className='text-danger mt-2 fs-7'>{fileError}</div>}
                </div>
              </>
            )}
          </div>
        </div>

        {uploadedFile &&
          extractedGrant &&
          Object.keys(aiFilledFields).some((key) => aiFilledFields[key]) && (
            <div
              className='alert alert-info d-flex align-items-center mb-4'
              style={{ backgroundColor: '#e8f4f8', borderColor: '#1976d2' }}
            >
              <i className='bi bi-robot fs-3 me-3'></i>
              <div>
                <strong>AI Data Extraction Successful!</strong>
                <br />
                <small>
                  Fields highlighted have been automatically filled from the uploaded file. You can
                  review and modify them as needed.
                </small>
              </div>
            </div>
          )}

        <div className='separator mt-1 mb-4'></div>
        <form onSubmit={formik.handleSubmit} className='d-flex flex-column gap-5 gap-md-7'>
          <div className='card border border-gray-300 rounded-2 mb-5 mb-md-0'>
            <div className='card-title pt-4 ms-5'>
              <h5 className='card-title fw-bold mb-0 fs-6 fs-md-5'>Basic Grant Information</h5>
            </div>
            <div className='card-body row g-4 g-md-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                  Company Name
                </label>
                <div className='position-relative'>
                  <CreatableSelect
                    styles={selectCustomStyles}
                    placeholder='Start typing company name...'
                    inputId='aria-company-input'
                    name='aria-company'
                    options={companyOptions}
                    isClearable
                    onInputChange={(e) => {
                      if (e.length >= 3) getSearchedCompanies(e)
                    }}
                    onBlur={() => formik.setFieldTouched('companyName', true)}
                    value={
                      companyOptions.find(
                        (opt: { companyName: string }) =>
                          opt.companyName === formik.values.companyName
                      ) ??
                      (formik.values.companyName
                        ? { label: formik.values.companyName, value: formik.values.companyName }
                        : null)
                    }
                    onChange={(e) => {
                      handleFieldChange('companyName', '')
                      handleFieldChange('companyCurrency', '')

                      if (!e) {
                        formik.setFieldValue('companyName', '')
                        formik.setFieldValue('companyCurrency', 'INR - ₹')
                        formik.setFieldValue('cin', '')
                        formik.setFieldValue('companyFMV', '')
                        formik.setFieldValue('companyDate', '')
                        setHissaCompanyId('')
                        return
                      }

                      formik.setFieldValue('companyName', e.value || e.label || '')

                      const selected = companyOptions.find(
                        (company: { companyName: any }) => company.companyName === e.value
                      )

                      if (selected) {
                        formik.setFieldValue('companyCurrency', selected.currency ?? 'INR - ₹')
                        formik.setFieldValue('cin', selected.cin)
                        formik.setFieldValue('companyFMV', selected.fmv?.toString() ?? '')
                        formik.setFieldValue(
                          'companyDate',
                          selected.fmvDate
                            ? new Date(selected.fmvDate).toISOString().split('T')[0]
                            : ''
                        )
                        setHissaCompanyId(selected.hissaCompanyId)
                        if (!selected.fmv) {
                          toast.warn('FMV data is not available for this company.')
                        }
                      } else {
                        formik.setFieldValue('companyCurrency', 'INR - ₹')
                        formik.setFieldValue('cin', '')
                        formik.setFieldValue('companyFMV', '')
                        formik.setFieldValue('companyDate', '')
                        setHissaCompanyId('')
                      }
                    }}
                    className='form-control p-0 border-0'
                  />
                </div>
                {formik.touched.companyName && formik.errors.companyName && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>{formik.errors.companyName}</div>
                )}
              </div>

              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                  Company Currency
                </label>
                <div className='position-relative'>
                  <select
                    name='companyCurrency'
                    className='form-control form-control-sm form-select '
                    value={formik.values.companyCurrency}
                    onChange={(e) => {
                      handleFieldChange('companyCurrency', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    style={getAiHighlightStyles('companyCurrency')}
                  >
                    {currencyList.map((currency) => (
                      <option key={currency.value} value={currency.value}>
                        {currency.label}
                      </option>
                    ))}
                  </select>
                </div>
                {formik.touched.companyCurrency && formik.errors.companyCurrency && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>
                    {formik.errors.companyCurrency}
                  </div>
                )}
              </div>

              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                  Plan Name
                </label>
                <div className='position-relative'>
                  <input
                    type='text'
                    name='planName'
                    className='form-control form-control-sm'
                    placeholder='e.g., Employee Stock Option Plan 2022'
                    value={formik.values.planName}
                    onChange={(e) => {
                      handleFieldChange('planName', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={getAiHighlightStyles('planName')}
                  />
                </div>
                {formik.touched.planName && formik.errors.planName && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>{formik.errors.planName}</div>
                )}
              </div>

              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                  Grant Type
                </label>
                <div className='position-relative'>
                  <select
                    name='grantType'
                    className='form-control form-control-sm form-select'
                    value={formik.values.grantType}
                    onChange={(e) => {
                      handleFieldChange('grantType', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    style={getAiHighlightStyles('grantType')}
                  >
                    <option value=''>Select Type</option>
                    {Object.values(GrantType).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                {formik.touched.grantType && formik.errors.grantType && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>{formik.errors.grantType}</div>
                )}
              </div>

              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                  Grant Date
                </label>
                <div className='position-relative'>
                  <input
                    type='date'
                    name='grantDate'
                    className='form-control form-control-sm'
                    value={formik.values.grantDate}
                    onChange={(e) => {
                      handleFieldChange('grantDate', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={getAiHighlightStyles('grantDate')}
                  />
                </div>
                {formik.touched.grantDate && formik.errors.grantDate && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>{formik.errors.grantDate}</div>
                )}
              </div>

              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                  Number of Options
                </label>
                <div className='position-relative'>
                  <input
                    type='number'
                    name='numberGranted'
                    className='form-control form-control-sm'
                    value={formik.values.numberGranted}
                    onChange={(e) => {
                      handleFieldChange('numberGranted', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={getAiHighlightStyles('numberGranted')}
                  />
                </div>
                {formik.touched.numberGranted && formik.errors.numberGranted && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>{formik.errors.numberGranted}</div>
                )}
              </div>

              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                  Strike Price
                </label>
                <div className='position-relative'>
                  <input
                    type='number'
                    name='exercisePriceOrSarValue'
                    className='form-control form-control-sm'
                    value={formik.values.exercisePriceOrSarValue}
                    onChange={(e) => {
                      handleFieldChange('exercisePriceOrSarValue', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={getAiHighlightStyles('exercisePriceOrSarValue')}
                  />
                </div>
                <div className='form-text fs-8 fs-md-7'>
                  The price you pay to exercise each option
                </div>
                {formik.touched.exercisePriceOrSarValue &&
                  formik.errors.exercisePriceOrSarValue && (
                    <div className='text-danger mt-1 fs-8 fs-md-7'>
                      {formik.errors.exercisePriceOrSarValue}
                    </div>
                  )}
              </div>

              <div className='col-12'>
                <div className='form-check'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='grantRunningCheckbox'
                    checked={isGrantRunning}
                    onChange={() => {
                      const newGrantRunningState = !isGrantRunning
                      setIsGrantRunning(newGrantRunningState)
                      if (newGrantRunningState) {
                        formik.setFieldValue(
                          'vestingTemplateName',
                          formik.values.vestingTemplateName
                        )
                        formik.setFieldValue('vestedOptions', formik.values.vestedOptions)
                        formik.setFieldValue('amountExercised', formik.values.amountExercised)
                        formik.setFieldValue(
                          'cliffDurationMonths',
                          formik.values.cliffDurationMonths
                        )
                        formik.setFieldValue('vestingFrequency', formik.values.vestingFrequency)
                        formik.setFieldValue(
                          'totalVestingDurationMonths',
                          formik.values.totalVestingDurationMonths
                        )
                        formik.setFieldValue('expiryNumber', null)
                        formik.setFieldValue('expiryUnitType', null)
                        formik.setFieldValue('expiryDate', null)
                      } else {
                        formik.setFieldValue('vestingTemplateName', '')
                        formik.setFieldValue('vestedOptions', '')
                        formik.setFieldValue('amountExercised', '')
                        formik.setFieldValue('cliffDurationMonths', '')
                        formik.setFieldValue('vestingFrequency', '')
                        formik.setFieldValue('totalVestingDurationMonths', '')
                      }
                    }}
                  />
                  <label
                    className='form-check-label fw-bold text-gray-800 ms-2 fs-7 fs-md-6'
                    htmlFor='grantRunningCheckbox'
                  >
                    Grant is currently running
                  </label>
                  <div className='text-muted mt-1 fs-8 fs-md-7'>
                    Check this box if your vesting is currently active and you are still accruing
                    shares according to your vesting schedule. This typically means you are still
                    employed at the company and the vesting period has not yet completed. If
                    unchecked, you'll need to specify when the grant expires.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='card border border-gray-300 rounded-2 mb-5 mb-md-0'>
            <div className='card-title pt-4 ms-5'>
              <h5 className='card-title fw-bold mb-0 fs-6 fs-md-5'>Vesting Information</h5>
            </div>
            <div className='card-body row g-4 g-md-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold fs-7 fs-md-6'>Vesting Template</label>
                <div className='position-relative'>
                  <select
                    name='vestingTemplateName'
                    className='form-control form-control-sm form-select'
                    value={formik.values.vestingTemplateName}
                    onChange={(e) => {
                      const selected = e.target.value as keyof typeof vestingScheduleMap
                      handleFieldChange('vestingTemplateName', selected)
                      formik.setFieldValue('vestingTemplateName', selected)
                      if (selected) {
                        const mapping = vestingScheduleMap[selected]
                        if (mapping) {
                          formik.setFieldValue(
                            'cliffDurationMonths',
                            mapping.cliffDurationMonths.toString()
                          )
                          formik.setFieldValue('vestingFrequency', mapping.frequency)
                          formik.setFieldValue(
                            'totalVestingDurationMonths',
                            mapping.totalVestingDurationMonths.toString()
                          )
                          if (formik.values.grantDate && formik.values.numberGranted) {
                            const vested = calculateVestedOptions(
                              new Date(formik.values.grantDate),
                              Number(formik.values.numberGranted),
                              mapping.frequency,
                              mapping.cliffDurationMonths,
                              mapping.totalVestingDurationMonths,
                              new Date()
                            )
                            formik.setFieldValue('vestedOptions', vested.toString())
                          }
                        }
                      } else {
                        formik.setFieldValue('cliffDurationMonths', '')
                        formik.setFieldValue('vestingFrequency', '')
                        formik.setFieldValue('totalVestingDurationMonths', '')
                        formik.setFieldValue('vestedOptions', '')
                      }
                    }}
                    disabled={!isGrantRunning}
                    style={getAiHighlightStyles('vestingTemplateName')}
                  >
                    <option value=''>Manual</option>
                    {Object.keys(vestingScheduleMap).map((template) => (
                      <option key={template} value={template}>
                        {template}
                      </option>
                    ))}
                  </select>
                </div>
                {formik.touched.vestingTemplateName && formik.errors.vestingTemplateName && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>
                    {formik.errors.vestingTemplateName}
                  </div>
                )}
              </div>

              {formik.values.vestingTemplateName && isGrantRunning && (
                <div className='col-12 col-md-6'>
                  <label className='form-label mb-1 fw-semibold fs-7 fs-md-6'>
                    Calculated Vested Options
                  </label>
                  <div className='position-relative'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      value={formik.values.vestedOptions || '0'}
                      readOnly
                      style={getAiHighlightStyles('vestedOptions')}
                    />
                  </div>
                  <div className='form-text fs-8 fs-md-7'>
                    Automatically calculated based on vesting template
                  </div>
                </div>
              )}

              {(!formik.values.vestingTemplateName || !isGrantRunning) && (
                <div className='col-12 col-md-6'>
                  <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                    Options Vested
                  </label>
                  <div className='position-relative'>
                    <input
                      type='number'
                      name='vestedOptions'
                      className='form-control form-control-sm'
                      value={formik.values.vestedOptions}
                      onChange={(e) => {
                        handleFieldChange('vestedOptions', e.target.value)
                        formik.handleChange(e)
                      }}
                      onBlur={formik.handleBlur}
                      placeholder='Enter vested options'
                      onWheel={(e) => e.currentTarget.blur()}
                      style={getAiHighlightStyles('vestedOptions')}
                    />
                  </div>
                  <div className='form-text fs-8 fs-md-7'>
                    Number of options that have already vested
                  </div>
                  {formik.touched.vestedOptions && formik.errors.vestedOptions && (
                    <div className='text-danger mt-1 fs-8 fs-md-7'>
                      {formik.errors.vestedOptions}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className='card border border-gray-300 rounded-2 mb-5 mb-md-0'>
            <div className='card-title pt-4 ms-5'>
              <h5 className='card-title fw-bold mb-0 fs-6 fs-md-5'>Exercise Information</h5>
            </div>
            <div className='card-body row g-4 g-md-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold required fs-7 fs-md-6'>
                  Options Exercised
                </label>
                <div className='position-relative'>
                  <input
                    name='amountExercised'
                    type='number'
                    className='form-control form-control-sm'
                    onChange={(e) => {
                      handleFieldChange('amountExercised', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.amountExercised}
                    style={getAiHighlightStyles('amountExercised')}
                  />
                </div>
                <small className='text-muted fs-8 fs-md-7'>
                  Number of options that have already been exercised
                </small>
                {formik.touched.amountExercised && formik.errors.amountExercised && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>
                    {formik.errors.amountExercised}
                  </div>
                )}
              </div>

              {!isGrantRunning && (
                <div className='col-12 col-md-6'>
                  <label className='form-label mb-1 fw-semibold fs-7 fs-md-6'>Expiry Period</label>
                  <div className='d-flex gap-2'>
                    <div className='position-relative flex-grow-1'>
                      <input
                        type='number'
                        name='expiryNumber'
                        value={formik.values.expiryNumber}
                        onChange={(e) => {
                          handleFieldChange('expiryNumber', e.target.value)
                          formik.handleChange(e)
                        }}
                        onBlur={formik.handleBlur}
                        className='form-control form-control-sm'
                        style={getAiHighlightStyles('expiryNumber')}
                      />
                    </div>
                    <div className='position-relative'>
                      <select
                        name='expiryUnitType'
                        value={formik.values.expiryUnitType}
                        onChange={(e) => {
                          handleFieldChange('expiryUnitType', e.target.value)
                          formik.handleChange(e)
                        }}
                        onBlur={formik.handleBlur}
                        className='form-control form-control-sm form-select'
                        style={{ maxWidth: '120px', ...getAiHighlightStyles('expiryUnitType') }}
                      >
                        <option value=''>Select</option>
                        <option value='DAYS'>Days</option>
                        <option value='MONTHS'>Months</option>
                        <option value='YEARS'>Years</option>
                      </select>
                    </div>
                  </div>
                  <small className='text-muted fs-8 fs-md-7'>
                    Time from grant date until options expire
                  </small>
                </div>
              )}
            </div>
          </div>

          <div className='card border border-gray-300 rounded-2 mb-5 mb-md-0'>
            <div className='card-title pt-4 ms-5'>
              <h5 className='card-title fw-bold mb-0 fs-6 fs-md-5'>Fair Market Value</h5>
            </div>
            <div className='card-body row g-4 g-md-5'>
              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold fs-7 fs-md-6'>
                  Fair Market Value <span className='text-muted'>(Optional)</span>
                </label>
                <div className='position-relative'>
                  <input
                    name='companyFMV'
                    type='number'
                    className='form-control form-control-sm'
                    placeholder='e.g., 12.50'
                    value={formik.values.companyFMV}
                    onChange={(e) => {
                      handleFieldChange('companyFMV', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={getAiHighlightStyles('companyFMV')}
                  />
                </div>
                {formik.touched.companyFMV && formik.errors.companyFMV && (
                  <div className='text-danger mt-1 fs-8 fs-md-7'>{formik.errors.companyFMV}</div>
                )}
                <small className='text-muted fs-8 fs-md-7'>Current value per share</small>
              </div>
              <div className='col-12 col-md-6'>
                <label className='form-label mb-1 fw-semibold fs-7 fs-md-6'>
                  FMV Date <span className='text-muted'>(Optional)</span>
                </label>
                <div className='position-relative'>
                  <input
                    type='date'
                    name='companyDate'
                    className='form-control form-control-sm'
                    value={formik.values.companyDate}
                    onChange={(e) => {
                      handleFieldChange('companyDate', e.target.value)
                      formik.handleChange(e)
                    }}
                    onBlur={formik.handleBlur}
                    onWheel={(e) => e.currentTarget.blur()}
                    style={getAiHighlightStyles('companyDate')}
                  />
                </div>
                <small className='text-muted fs-8 fs-md-7'>Date when the FMV was determined</small>
              </div>
            </div>
          </div>

          <div className='card border border-gray-300 rounded-2 mb-5 mb-md-0'>
            <div className='card-title pt-4 ms-5'>
              <h5 className='card-title fw-bold mb-0 fs-6 fs-md-5'>Additional Information</h5>
            </div>
            <div className='card-body'>
              <label className='form-label mb-1 fw-semibold fs-7 fs-md-6'>
                Notes <span className='text-muted'>(Optional)</span>
              </label>
              <div className='position-relative'>
                <textarea
                  name='notes'
                  className='form-control form-control-sm'
                  placeholder='Any additional notes about this grant...'
                  onChange={(e) => {
                    handleFieldChange('notes', e.target.value)
                    formik.handleChange(e)
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.notes}
                  rows={3}
                  style={getAiHighlightStyles('notes')}
                />
              </div>
              {formik.touched.notes && formik.errors.notes && (
                <div className='text-danger mt-1 fs-8 fs-md-7'>{formik.errors.notes}</div>
              )}
              <div className='form-text fs-8 fs-md-7'>
                {formik.values.notes?.length || 0}/500 characters
              </div>
            </div>
          </div>

          <div className='d-flex justify-content-end border-top pt-4 gap-3 bg-white'>
            <button type='button' className='btn btn-light btn-sm' onClick={props.handleClose}>
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary btn-sm'
              disabled={loading || !formik.isValid}
            >
              {loading ? 'Saving...' : props.isEdit ? 'Update Grant' : 'Add Grant'}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}